import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Pencil } from 'lucide-react'
import { api } from '@/lib/api'
import { EntityEditForm } from '@/components/EntityEditForm'

interface SearchableEntitySelectProps {
  label: string
  value: number | undefined
  onChange: (value: number | undefined) => void
  fetchUrl: string
  placeholder: string
  entityType: 'architect' | 'developer' | 'broker'
  required?: boolean
}

export function SearchableEntitySelect({
  label,
  value,
  onChange,
  fetchUrl,
  placeholder,
  entityType,
  required = false
}: SearchableEntitySelectProps) {
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null)

  // Fetch ALL entities once using the lightweight /dropdown endpoint
  const { data: allEntities, isLoading } = useQuery({
    queryKey: [`${fetchUrl}-dropdown`],
    queryFn: async () => {
      const response = await api.get(`${fetchUrl}/dropdown`)
      return response.data?.data || response.data || []
    },
    staleTime: 5 * 60 * 1000,
  })

  const entities = allEntities || []

  const getEntityId = (entity: any) => {
    return entity?.architect_id ?? entity?.developer_id ?? entity?.broker_id ?? entity?.id
  }

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value
    if (selectedValue === '') {
      onChange(undefined)
    } else {
      onChange(Number(selectedValue))
    }
  }

  const handleEntitySaved = (entity: { id?: number; name: string }) => {
    if (modalMode === 'create' && entity.id != null) {
      onChange(entity.id)
    }
    setModalMode(null)
  }

  const inputId = `project-${label.toLowerCase().replace(/\s+/g, '-')}`
  const lower = label.toLowerCase()

  return (
    <div className="space-y-2">
      <label htmlFor={inputId} className="form-label">
        {label} {required && <span className="text-danger-500">*</span>}
      </label>
      <div className="flex gap-2">
        <select
          id={inputId}
          name={inputId}
          className="form-input flex-1"
          value={value ?? ''}
          onChange={handleChange}
          disabled={isLoading}
        >
          <option value="">{isLoading ? 'Loading...' : placeholder}</option>
          {entities.map((entity: any) => (
            <option key={getEntityId(entity)} value={getEntityId(entity)}>
              {entity.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setModalMode('create')}
          className="btn-secondary flex items-center justify-center px-3"
          title={`Add ${lower}`}
          aria-label={`Add ${lower}`}
        >
          <Plus className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => setModalMode('edit')}
          className="btn-secondary flex items-center justify-center px-3 disabled:opacity-40 disabled:cursor-not-allowed"
          title={`Edit ${lower}`}
          aria-label={`Edit ${lower}`}
          disabled={value == null}
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>
      {modalMode !== null && (
        <EntityEditForm
          type={entityType}
          entityId={modalMode === 'edit' ? value ?? null : null}
          onClose={() => setModalMode(null)}
          onSuccess={handleEntitySaved}
        />
      )}
    </div>
  )
}
